import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const SearchModal = ({ isOpen, onClose, surahs }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen]);

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [isOpen]);

  // Perform search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    setIsSearching(true);

    // Search in surah metadata
    const metadataResults = surahs.filter(surah => {
      // Search by number
      if (surah.number.toString() === query) return true;
      
      // Search by name (Arabic + English)
      if (surah.name.toLowerCase().includes(query)) return true;
      if (surah.englishName.toLowerCase().includes(query)) return true;
      if (surah.englishNameTranslation.toLowerCase().includes(query)) return true;
      
      return false;
    }).map(surah => ({
      type: 'surah',
      surah: surah,
      ayah: null,
    }));

    // For now, we only search metadata (Surah names/numbers)
    // Full ayah text search would require fetching all surahs or a backend search API
    setSearchResults(metadataResults);
    setIsSearching(false);
  }, [searchQuery, surahs]);

  const handleResultClick = (result) => {
    if (result.type === 'surah') {
      navigate(`/surah/${result.surah.number}`);
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-start justify-center"
      style={{
        background: 'rgba(0, 0, 0, 0.7)',
        paddingTop: 'calc(80px + var(--safe-area-top))',
        paddingBottom: 'var(--safe-area-bottom)',
      }}
      onClick={onClose}
    >
      {/* Modal Content */}
      <div 
        className="w-full max-w-2xl mx-4 rounded-xl shadow-2xl overflow-hidden"
        style={{
          background: 'var(--bg-elevated)',
          border: '2px solid var(--border-default)',
          maxHeight: 'calc(var(--app-height) - 100px - var(--safe-area-top) - var(--safe-area-bottom))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Header */}
        <div 
          className="p-4 border-b"
          style={{
            borderColor: 'var(--border-default)',
          }}
        >
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search by Surah name, number..."
                className="w-full px-4 py-3 rounded-lg text-base transition-all"
                style={{
                  background: 'var(--bg-primary)',
                  border: '2px solid var(--border-strong)',
                  color: 'var(--text-english)',
                  outline: 'none',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--gold-primary)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border-strong)';
                }}
              />
              {isSearching && (
                <div 
                  className="absolute right-4 top-1/2 transform -translate-y-1/2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Searching...
                </div>
              )}
            </div>
            
            <button
              onClick={onClose}
              className="px-4 py-3 rounded-lg font-medium text-sm transition-all active:scale-95"
              style={{
                background: 'var(--button-default)',
                border: '2px solid var(--border-strong)',
                color: 'var(--text-english)',
                minHeight: '48px', // Enhanced touch target
              }}
            >
              Close
            </button>
          </div>
          
          <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
            Press ESC to close
          </p>
        </div>

        {/* Search Results */}
        <div 
          className="overflow-y-auto"
          style={{
            maxHeight: 'calc(var(--app-height) - 220px - var(--safe-area-top) - var(--safe-area-bottom))',
          }}
        >
          {!searchQuery.trim() && (
            <div className="p-8 text-center">
              <div className="text-4xl mb-3">🔍</div>
              <p className="text-base font-medium" style={{ color: 'var(--text-english)' }}>
                Start typing to search
              </p>
              <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
                Search by Surah name (Arabic/English) or number
              </p>
            </div>
          )}

          {searchQuery.trim() && searchResults.length === 0 && !isSearching && (
            <div className="p-8 text-center">
              <div className="text-4xl mb-3">🤷</div>
              <p className="text-base font-medium" style={{ color: 'var(--text-english)' }}>
                No results found
              </p>
              <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
                Try different keywords or Surah number
              </p>
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="p-4 space-y-2">
              {searchResults.map((result, index) => (
                <button
                  key={index}
                  onClick={() => handleResultClick(result)}
                  className="w-full p-4 rounded-lg text-left transition-all active:scale-95"
                  style={{
                    background: 'var(--bg-primary)',
                    border: '2px solid var(--border-default)',
                    minHeight: '72px', // Enhanced touch target
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--gold-muted)';
                    e.currentTarget.style.borderColor = 'var(--gold-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--bg-primary)';
                    e.currentTarget.style.borderColor = 'var(--border-default)';
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Surah Number */}
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center font-bold"
                        style={{
                          background: 'var(--gold-muted)',
                          border: '2px solid var(--gold-primary)',
                          color: 'var(--gold-hover)',
                        }}
                      >
                        {result.surah.number}
                      </div>
                      
                      {/* Surah Info */}
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span 
                            className="text-xl font-bold text-arabic"
                            style={{ color: 'var(--text-arabic)' }}
                          >
                            {result.surah.name}
                          </span>
                          <span 
                            className="text-base font-semibold"
                            style={{ color: 'var(--text-english)' }}
                          >
                            {result.surah.englishName}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          <span>{result.surah.englishNameTranslation}</span>
                          <span>•</span>
                          <span>{result.surah.revelationType}</span>
                          <span>•</span>
                          <span>{result.surah.numberOfAyahs} Ayahs</span>
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ color: 'var(--gold-primary)' }}>→</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;

